import React, { createContext, useState, useContext, useCallback } from 'react';
import { billingAPI } from '../services/api';
import { openExternalUrl } from '../utils/nativeBrowser';
import { BillingStatus, SubscriptionPlan } from '../types';
import { formatBytes } from '../utils/formatters';
import { unwrapResponse } from '../utils/unwrapResponse';

/** Extract error message from Axios-shaped error objects */
function extractErrorMsg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } } } | undefined;
  return e?.response?.data?.error || fallback;
}

interface BillingContextType {
  billingStatus: BillingStatus | null;
  plans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  billingEnabled: boolean;
  fetchBillingStatus: () => Promise<void>;
  fetchPlans: () => Promise<void>;
  createCheckoutSession: (planSlug: string) => Promise<{ url: string } | null>;
  openCustomerPortal: () => Promise<void>;
  cancelSubscription: (immediately?: boolean) => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  formatStorage: (bytes: number) => string;
  formatPrice: (cents: number, interval?: string | null) => string;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingEnabled, setBillingEnabled] = useState(false);

  const fetchBillingStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingAPI.getStatus();
      setBillingStatus(unwrapResponse<any>(response.data));
      setBillingEnabled(unwrapResponse<any>(response.data).billingEnabled || false);
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to fetch billing status'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingAPI.getPlans();
      setPlans(unwrapResponse<any>(response.data).plans || []);
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to fetch plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  const createCheckoutSession = useCallback(async (planSlug: string): Promise<{ url: string } | null> => {
    setLoading(true);
    setError(null);
    try {
      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing`;

      const response = await billingAPI.createCheckout({
        planSlug,
        successUrl,
        cancelUrl,
      });

      return unwrapResponse<any>(response.data);
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to create checkout session'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/billing`;
      const response = await billingAPI.createPortal({ returnUrl });

      const portalUrl = unwrapResponse<any>(response.data).url;
      if (portalUrl) {
        await openExternalUrl(portalUrl);
      }
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to open billing portal'));
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelSubscription = useCallback(async (immediately: boolean = false) => {
    setLoading(true);
    setError(null);
    try {
      await billingAPI.cancel({ immediately });
      await fetchBillingStatus();
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to cancel subscription'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBillingStatus]);

  const reactivateSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await billingAPI.reactivate();
      await fetchBillingStatus();
    } catch (err: unknown) {
      setError(extractErrorMsg(err, 'Failed to reactivate subscription'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchBillingStatus]);

  const formatStorage = useCallback((bytes: number): string => {
    return formatBytes(bytes);
  }, []);

  const formatPrice = useCallback((cents: number, interval?: string | null): string => {
    const dollars = (cents / 100).toFixed(2);
    if (interval) {
      return `$${dollars}/${interval}`;
    }
    return `$${dollars}`;
  }, []);

  return (
    <BillingContext.Provider
      value={{
        billingStatus,
        plans,
        loading,
        error,
        billingEnabled,
        fetchBillingStatus,
        fetchPlans,
        createCheckoutSession,
        openCustomerPortal,
        cancelSubscription,
        reactivateSubscription,
        formatStorage,
        formatPrice,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};
